import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import useSentry from "Pods/Shared/Hooks/useSentry"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { setViewerFileFormat } from "Pods/ThreeDStudio/Store/actions/viewer"

import {
  BACKGROUND_IMAGES_QUERY_KEY,
  fetchBackgroundImages,
} from "../Components/EnvironmentSettings/Hooks/useBackgroundImages"
import { applyInitialSettings } from "../Utils/applyInitialSettings"
import { applyTopologyMode } from "../Utils/applyTopologyMode"
import { freezeFrame, unfreezeFrame } from "../Utils/frozenFrameSwap"
import { loadModelSource } from "../Utils/loadModelSource"
import { reportModelLoadFailure } from "../Utils/reportModelLoadFailure"

const buildUrlToLoad = ({ url, pngUrl, mtlUrl }) => {
  const files = [url, pngUrl, mtlUrl].filter(Boolean)

  return files.length === 1 ? files[0] : files
}

// Loads the uploaded model, or the opened run's model, into the viewer and applies
// its initial settings. `isLoading` stays true from the first render until the
// model has painted or failed; `isOpenedRunOnScreen` is true only once the opened
// run's own mesh has painted.
export default function useModelLoader(viewer) {
  const queryClient = useQueryClient()
  const { captureException } = useSentry()
  // captureException's identity flips once Sentry finishes loading; keeping it
  // in a ref stops that from re-running the load effect and reloading the model.
  const captureExceptionRef = useRef(captureException)
  captureExceptionRef.current = captureException
  const [isLoading, setIsLoading] = useState(true)
  // The run whose mesh actually reached the screen. A plain painted/not-painted
  // flag is not enough: the load effect only clears it after the render that
  // switched runs, so on that render the flag would still describe the previous
  // run and the ping would fire for the new one before it paints. Comparing ids
  // makes the gate false from that very render. It also stays false when a load
  // fails, where the loader flag clears without anything being painted.
  const [paintedRunId, setPaintedRunId] = useState(null)
  const openedRunId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const uploadedModelUrl = useThreeDStudioStore(state => state.modelSettings.inputThreeDUrl)
  const uploadedModelFormat = useThreeDStudioStore(state => state.viewer.fileFormat)
  const modelUrl = useThreeDStudioStore(state => state.ui.openedModel?.outputAssets?.[0]?.fileUrl)
  const materialPngUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'png')?.fileUrl
  )
  const materialMtlUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'mtl')?.fileUrl
  )
  const settings = useThreeDStudioStore(state => state.ui?.openedModel?.settings)
  const material = useThreeDStudioStore(state => state.viewer?.material)
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  const isSmartLowPoly = useThreeDStudioStore(
    state => state.modelSettings.isSmartLowPoly || state.remeshSettings.isSmartLowPoly
  )

  const modelData = uploadedModelUrl
    ? { url: uploadedModelUrl, format: uploadedModelFormat }
    : { url: modelUrl, pngUrl: materialPngUrl, mtlUrl: materialMtlUrl }

  const currentLoadRequestIdRef = useRef(0)
  const currentModelIdRef = useRef(null)
  // Serializes viewer mutations. `loadModel` adds the mesh to the scene before
  // it resolves (and before the model lands in `getLoadedModels`), so a newer
  // request's `unloadAllModels` cannot remove an older mesh that is still
  // mid-load. Chaining each request guarantees only one load runs at a time, so
  // two models can never paint at once when the user switches rapidly.
  const loadChainRef = useRef(Promise.resolve())

  // Unmounting mid-swap would otherwise leave the loop paused on a frame whose
  // model is already gone.
  useEffect(() => () => unfreezeFrame(viewer), [viewer])

  useEffect(() => {
    if (!viewer || viewer.isDisposed) {
      return
    }

    const currentViewer = viewer
    const { format } = modelData
    const urlToLoad = buildUrlToLoad(modelData)

    const requestId = currentLoadRequestIdRef.current + 1
    currentLoadRequestIdRef.current = requestId

    if (!urlToLoad) {
      loadChainRef.current = loadChainRef.current.then(() => {
        if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
          return
        }
        try {
          if (currentModelIdRef.current) {
            currentViewer.unloadModel(currentModelIdRef.current)
            currentModelIdRef.current = null
          } else {
            currentViewer.unloadAllModels()
          }
        } catch {
          // Ignore unload failures; viewer state will be corrected on next update.
        }
        unfreezeFrame(currentViewer)
        setIsLoading(false)
        setPaintedRunId(null)
      }).catch(() => {})
      return
    }

    setIsLoading(true)
    setPaintedRunId(null)

    // Most often a CORS-blocked asset response: the model never paints and the
    // failure is otherwise invisible, so it goes to Sentry. Deliberately not
    // awaited: the report runs a reachability probe to tell a CORS block apart
    // from a real outage, and the failure has to surface now so the loader
    // clears. It never rejects. Reports the asset URL, not the blob, so the
    // entry is traceable.
    const handleLoadFailure = (error) => {
      reportModelLoadFailure(captureExceptionRef.current, error, {
        url: urlToLoad,
        format,
        providerModelId: openedModelId,
        isUploadedModel: Boolean(uploadedModelUrl),
      })
    }

    loadChainRef.current = loadChainRef.current.then(() => {
      // Superseded before this request reached the head of the chain: skip the
      // load entirely so rapid switching only ever loads the latest model.
      if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
        return undefined
      }

      // Anything already on screen is worth holding, whatever the incoming model
      // is and however long it takes to arrive. The render loop is paused while it
      // is held, so the viewport is static and camera input has no visible effect
      // until the new model paints - accepted deliberately, because a held frame
      // under the loader reads better than a blank panel even on a cold download.
      //
      // Nothing may paint between the freeze and the unload: the freeze holds the
      // outgoing frame that the unload is about to invalidate.
      const hasPaintedModel = currentModelIdRef.current != null || Boolean(currentViewer.isPaused)

      if (hasPaintedModel) {
        freezeFrame(currentViewer)
      } else {
        unfreezeFrame(currentViewer)
      }

      try {
        currentViewer.unloadAllModels()
        currentModelIdRef.current = null
      } catch {
        // Ignore unload failures; viewer state will be corrected on next update.
      }

      return Promise.all([
        loadModelSource(currentViewer, urlToLoad, format, handleLoadFailure),
        // Resolves the cached background-images list (fetches once if missing)
        // so applyInitialSettings can map surroundingId -> background_url.
        // Falls back to [] on failure to keep the rest of the apply unblocked.
        queryClient.ensureQueryData({
          queryKey: BACKGROUND_IMAGES_QUERY_KEY,
          queryFn: fetchBackgroundImages,
        }).catch(() => []),
      ])
        .then(([modelEntry, images]) => {
          if (currentLoadRequestIdRef.current !== requestId) {
            try {
              currentViewer.unloadModel(modelEntry.id)
            } catch {
              // Ignore unload failures for outdated model.
            }
            return
          }

          currentModelIdRef.current = modelEntry.id
          setViewerFileFormat(modelEntry.format)

          applyTopologyMode(currentViewer)
          applyInitialSettings(currentViewer, settings, images, material, openedModelId, isSmartLowPoly)
          requestAnimationFrame(() => {
            if (currentLoadRequestIdRef.current === requestId) {
              // Batched with the loader clear below, so revealing the incoming
              // model cannot expose a frame of empty viewport.
              unfreezeFrame(currentViewer)
              setIsLoading(false)
              // openedRunId is read from the render that scheduled this load, so
              // it always names the run these assets belong to.
              setPaintedRunId(openedRunId ?? null)
            }
          })
        })
        .catch(() => {
          if (currentLoadRequestIdRef.current === requestId) {
            // Ignore load failure; viewer will attempt to load again on next update.
            // The held frame goes with it - there is no incoming model to reveal,
            // and leaving the loop paused would strand a model the viewer dropped.
            unfreezeFrame(currentViewer)
            setIsLoading(false)
          }
        })
    }).catch(() => {})
  }, [
    viewer,
    modelData.url,
    modelData.pngUrl,
    modelData.mtlUrl,
    modelData.format,
  ])

  // An uploaded model wins over ui.openedModel but does not clear it, so the
  // opened run is off screen whenever one is displayed.
  const isOpenedRunOnScreen = paintedRunId != null && paintedRunId === openedRunId && !uploadedModelUrl

  return { isLoading, isOpenedRunOnScreen }
}
